import React from 'react';
import { Redirect } from 'react-router-dom';

import {
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  useTheme,
} from '@mui/material';
import { AppsOutlined, SecurityOutlined } from '@mui/icons-material';
import { Documents } from '../_tabs/documentsTab/Documents';
import { CustomerManage } from './manage/CustomerManage';
import MyAgent from '../../myAgent/MyAgent';
import { PoliciesBadge } from '../_global/badges/PoliciesBadge';
import ConnectorDetailsContainer from '../../policyholders/connectors/ConnectorDetailsContainer';
import ConnectorsPage from '../../policyholders/connectors/ConnectorsPage';
import { CustomerDashboard } from './dashboard/CustomerDashboard';
import { CoverageStates } from './dashboard/coverage/CoverageStates';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import NotFound from '../../common/NotFound';

// icons
import AgentsIcon from '../../_assets/svg/Agents.svg';
import ConnectorsIcon from '../../_assets/svg/Connectors.svg';
import TrainingIcon from '../../_assets/svg/training.svg';
import DashboardIcon from '../../_assets/svg/Dashboard.svg';
import PoliciesIcon from '../../_assets/svg/Policies.svg';
import ManageIcon from '../../_assets/svg/Manage.svg';
import InsightIcon from '../../_assets/svg/Insights.svg';
import DocumentsIcon from '../../_assets/svg/Documents.svg';

import InsightsContainer from '../../policyholders/insights/InsightsContainer';
import { ApproveQuote } from './quotes/ApproveQuote';
import { SignQuote } from './quotes/SignQuote';
import { AttestQuote } from './quotes/AttestQuote';
import { OneIncPayPolicy } from './policies/OneIncPayPolicy';
import { NameChange } from './quotes/NameChange';
import CustomerRenewalWorkflow from './renewals/CustomerRenewalWorkflow';
import { useCustomerManageRoutes } from './customer.manage.routes';
import { MidTermEndorsementStepper } from './renewals/MidtermEndorsementStepper';
import { ActivityListing } from '../activity/ActivityListing';

import DevelopersRouter from '../developers/DevelopersRouter';
import { useCustomerDeveloperRoutes } from './customer.developers.routes';
import { createSubtreeRoute } from '../../routes/createSubtreeRoute';
import { TrainingForCustomer } from '../../policyholders/training/TrainingForCustomer';
import SpotlightNotification from '../../components/spotlight/SpotlightNotification';
import ClaimsIcon from '../../_assets/svg/Claims.svg';
import { useCowbellTranslations } from '../../i18n/translations';
import ServiceCatalog from '../../policyholders/resilience/components/ServiceCatalog';
import ServiceIntro from '../../policyholders/resilience/components/ServiceIntro';
import ServicePricing from '../../policyholders/resilience/components/ServicePricing';
import MyServices from '../../policyholders/resilience/components/MyServices';
import { FEATURE_FLAG_KEYS } from '../../config/growthbook';
import {
  FNOL_CLAIM_DETAILS_ROUTE_PATH,
  FNOL_CLAIMS_LIST_PATH,
  FNOL_NEW_CLAIM_ROUTE_PATH,
} from './claims/fnol/fnolPaths';
import SpotlightDetails from '../../policyholders/insights/SpotlightDetails';

// claims
const Claims = React.lazy(() => import('./claims/Claims'));
const FileNewClaim = React.lazy(() => import('./claims/fnol/FileNewClaim'));
const AddClaimDetails = React.lazy(
  () => import('./claims/fnol/AddClaimDetails')
);
// With web intake switched off, /customer/claims/new still has to be claimed here: left to
// /customer/claims/:claimId it would try to open a claim with the id "new".
const ClaimsListRedirect = () => <Redirect to={FNOL_CLAIMS_LIST_PATH} />;

// api keys
const ApiKeys = React.lazy(() => import('../../admin/apiKeys/ApiKeys'));
const ApiKeyCreation = React.lazy(
  () => import('../../admin/apiKeys/ApiKeyCreation')
);
const ApiActivityTracking = React.lazy(
  () => import('../../apiKeys/ApiActivityTracking')
);

// quotes + policies
const PolicyHolderQuotesPoliciesListings = React.lazy(
  () =>
    import(
      '../quotes-policies/cards/policyholder/PolicyHolderQuotesPoliciesListings'
    )
);

// manage
const Users = React.lazy(() => import('../admin/users/Users'));

// misc
const ContactSupport = React.lazy(() => import('../support/ContactSupport'));

const PolicyHolderP100RenewalResolver = React.lazy(
  () =>
    import(
      '../workflows/accounts-renewals/prime100/policyholder/PolicyHolderPrime100RenewalWorkflow'
    )
);

const VRADashboard = React.lazy(
  () => import('../../policyholders/resilience/features/vra/views/VRADashboard')
);

const VendorDetails = React.lazy(
  () =>
    import('../../policyholders/resilience/features/vra/views/VendorDetails')
);

const CartPage = React.lazy(
  () =>
    import('../../policyholders/resilience/features/self-serve/views/CartPage')
);

const InvoicesPage = React.lazy(
  () =>
    import(
      '../../policyholders/resilience/features/self-serve/views/InvoicesPage'
    )
);

const SignAgreements = React.lazy(
  () =>
    import(
      '../../policyholders/resilience/features/self-serve/views/SignAgreements'
    )
);

const PaymentPage = React.lazy(
  () =>
    import(
      '../../policyholders/resilience/features/self-serve/views/PaymentPage'
    )
);

// Built once, at module scope: these are the two boundaries that own a whole
// subtree, so a fresh component type here remounts everything below it and
// discards any unsaved form state (FIR-21142).
const CustomerManageRoute = createSubtreeRoute(CustomerManage, {
  useRoutes: useCustomerManageRoutes,
});

const CustomerDevelopersRoute = createSubtreeRoute(DevelopersRouter, {
  useRoutes: useCustomerDeveloperRoutes,
  rootPath: 'customer',
});

export const useCustomerRoutes = () => {
  const customerManageRoutes = useCustomerManageRoutes();
  const customerDeveloperRoutes = useCustomerDeveloperRoutes();
  const theme = useTheme();
  const { t } = useCowbellTranslations();
  const crsEnable = useFeatureIsOn(FEATURE_FLAG_KEYS.CRS_ENABLE);
  const crsEnableSelfServe = useFeatureIsOn(
    FEATURE_FLAG_KEYS.CRS_ENABLE_SELF_SERVE
  );
  const isSpotlightV2Enabled = useFeatureIsOn(FEATURE_FLAG_KEYS.SPOTLIGHT_V2);
  const isOpenNewClaimOn = useFeatureIsOn(
    FEATURE_FLAG_KEYS.POLICYHOLDER_OPEN_NEW_CLAIM
  );

  return [
    {
      path: '/customer/dashboard',
      allow: 'any',
      component: CustomerDashboard,
      exact: true,
      rqps: ['before', 'after'],
      nav: {
        name: 'Dashboard',
        group: 'dashboard',
        icon: <DashboardIcon />,
      },
    },
    {
      path: '/customer/quotes/:quoteId/:step/:product/policies/:policyNum',
      allow: { quote: 'view' },
      component: MidTermEndorsementStepper,
      exact: true,
    },
    {
      path: '/customer/quotes/:quoteId/insuredName/:product',
      allow: { quote: 'manage' },
      component: NameChange,
      exact: true,
    },
    {
      path: '/customer/quotes/:quoteId/attest/:product',
      allow: { quote: 'manage' },
      component: AttestQuote,
    },
    {
      path: '/customer/quotes/:quoteId/renewals/:product/:step',
      allow: { quote: 'manage' },
      component: CustomerRenewalWorkflow,
    },
    {
      path: '/customer/quotes/:quoteId/renew/:step?',
      allow: { quote: 'manage' },
      component: PolicyHolderP100RenewalResolver,
    },
    {
      path: '/customer/quotes/:quoteId/approve/:product',
      allow: { quote: 'manage' },
      component: ApproveQuote,
      exact: true,
    },
    {
      path: '/customer/quotes/:quoteId/sign/:product',
      allow: { quote: 'manage' },
      component: SignQuote,
      exact: true,
    },
    // Development only: every state of the coverage tile, from the fixtures the
    // specs assert against. Not registered in production, and deliberately
    // absent from the nav.
    ...(process.env.NODE_ENV === 'production'
      ? []
      : [
          {
            path: '/customer/_dev/coverage-states',
            component: CoverageStates,
            allow: 'any',
            exact: true,
          },
        ]),
    {
      path: '/customer/policies',
      allow: { policy: 'view' },
      component: PolicyHolderQuotesPoliciesListings,
      rqps: ['size', 'orderBy', 'order'],
      exact: true,
      nav: {
        name: 'Policies',
        group: 'insurance',
        icon: (
          <PoliciesBadge>
            <PoliciesIcon />
          </PoliciesBadge>
        ),
      },
    },
    {
      path: '/customer/policies/:policyId/pay/oneinc/:product',
      allow: { policy: 'manage' },
      component: OneIncPayPolicy,
      exact: true,
    },
    // Sits between the Policies and Claims routes so the sidebar's `insurance`
    // group — which renders in array order — shows "My Agent" directly below
    // Policies.
    //
    // `allow: 'any'` rather than the pre-CRS-1174 `{ agencies: 'search' }`:
    // fir-login revokes SEARCH_AGENCIES in the assume-role/jump-in flow
    // (LoginService) and per account privacy list (RoleService), so that gate
    // hid the item from the policyholders it exists for. `allow` only controls
    // sidebar visibility — the page and the agency APIs enforce their own
    // permissions.
    {
      path: '/customer/myagent',
      component: MyAgent,
      allow: 'any',
      exact: true,
      rqps: ['size', 'orderBy', 'order'],
      nav: {
        name: `My ${t('agent')}`,
        group: 'insurance',
        icon: <AgentsIcon />,
      },
    },
    {
      path: '/customer/claims',
      component: Claims,
      allow: { claims: 'view' },
      exact: true,
      rqps: ['size', 'orderBy', 'order'],
      nav: {
        name: 'Claims',
        group: 'insurance',
        icon: <ClaimsIcon />,
      },
    },
    // Both registered ahead of /customer/claims/:claimId, which would otherwise swallow
    // /new and /:claimId/details as claim ids.
    {
      path: FNOL_NEW_CLAIM_ROUTE_PATH,
      component: isOpenNewClaimOn ? FileNewClaim : ClaimsListRedirect,
      allow: { claims: 'view' },
      exact: true,
    },
    {
      path: FNOL_CLAIM_DETAILS_ROUTE_PATH,
      component: AddClaimDetails,
      allow: { claims: 'view' },
      exact: true,
    },
    {
      path: '/customer/claims/:claimId',
      component: Claims,
      allow: { claims: 'view' },
      rqps: ['size', 'orderBy', 'order'],
    },
    {
      path: '/customer/connectors/:tab?',
      component: ConnectorsPage,
      allow: 'any',
      nav: {
        name: 'Connectors',
        group: 'resilience',
        groupOrder: 4,
        icon: <ConnectorsIcon />,
      },
    },
    {
      path: '/customer/insights',
      component: InsightsContainer,
      allow: { account: 'customer' },
      rqps: ['size', 'order', 'orderBy', 'before', 'after'],
      exact: true,
      nav: {
        name: 'Insights',
        group: 'resilience',
        groupOrder: 5,
        Component: ({ itemIconClasses, itemTextClasses, name }: any) => {
          return (
            <>
              <MuiListItemIcon classes={itemIconClasses}>
                <InsightIcon />
              </MuiListItemIcon>
              <MuiListItemText
                classes={itemTextClasses}
                disableTypography
                secondary={name}
              />
              <SpotlightNotification
                style={{ fontSize: '1.3rem' }}
                containerProps={{ style: { marginRight: '0.3rem' } }}
              />
            </>
          );
        },
      },
    },
    ...(isSpotlightV2Enabled
      ? [
          {
            path: '/customer/insights/:spotlightId',
            component: SpotlightDetails,
            allow: { account: 'customer' },
            exact: true,
          },
        ]
      : []),
    {
      path: '/customer/training',
      allow: { account: 'customer' },
      component: TrainingForCustomer,
      nav: {
        name: 'Training',
        group: 'resilience',
        groupOrder: 6,
        icon: <TrainingIcon />,
      },
    },
    ...(crsEnable
      ? [
          {
            path: '/customer/resilience/catalog/services/:productFamilyId/intro',
            allow: { account: 'customer' },
            component: ServiceIntro,
          },
          {
            path: '/customer/resilience/catalog/services/:productFamilyId/pricing',
            allow: { account: 'customer' },
            component: ServicePricing,
          },
          {
            path: '/customer/resilience/catalog',
            allow: { account: 'customer' },
            component: ServiceCatalog,
            nav: {
              name: 'Catalog',
              group: 'resilience',
              groupOrder: 0,
              icon: <AppsOutlined htmlColor={theme.palette.common.greyWhite} />,
            },
          },
          {
            path: '/customer/resilience/services',
            allow: { account: 'customer' },
            component: MyServices,
            nav: {
              name: 'My Services',
              group: 'resilience',
              groupOrder: 1,
              icon: (
                <SecurityOutlined htmlColor={theme.palette.common.greyWhite} />
              ),
            },
          },
          {
            path: '/customer/resilience/vra/dashboard',
            allow: { account: 'customer' },
            component: VRADashboard,
          },
          {
            path: '/customer/resilience/vra/vendors/:vendorId',
            allow: { account: 'customer' },
            component: VendorDetails,
          },
          ...(crsEnableSelfServe
            ? [
                {
                  path: '/customer/resilience/cart',
                  allow: { account: 'customer' },
                  component: CartPage,
                },
                {
                  path: '/customer/resilience/invoices',
                  allow: { account: 'customer' },
                  component: InvoicesPage,
                },
                {
                  path: '/customer/resilience/checkout/sign-agreements',
                  allow: { account: 'customer' },
                  component: SignAgreements,
                },
                {
                  path: '/customer/resilience/checkout/payment',
                  allow: { account: 'customer' },
                  component: PaymentPage,
                },
              ]
            : []),
        ]
      : []),
    {
      path: '/customer/documents',
      allow: { account: 'customer' },
      rqps: ['size', 'page', 'order', 'orderBy'],
      component: Documents,
      nav: {
        name: 'Documents',
        // Grouped with Manage rather than left in the footer: an account's own
        // documents belong with its own settings, not below the version number.
        group: 'manage',
        groupOrder: 1,
        icon: <DocumentsIcon />,
      },
    },
    {
      path: '/customer/connector/connector-details/:connectorType',
      component: ConnectorDetailsContainer,
      allow: { connector: 'view' },
      rqps: ['before', 'after', 'size'],
    },
    {
      path: '/customer/apiKeys/create',
      allow: { api_keys: 'view' },
      component: ApiKeyCreation,
    },
    {
      path: '/customer/apiKeys/activity/details',
      allow: { api_keys: 'view' },
      exact: true,
      component: ApiActivityTracking,
      rqps: ['size', 'dateFilter', 'before', 'after', 'orderBy', 'order'],
    },
    {
      path: '/customer/apiKeys',
      allow: { api_keys: 'view' },
      component: ApiKeys,
      rqps: ['size', 'order', 'orderBy'],
    },
    {
      path: '/customer/developers/:tab/:id?',
      allow: 'any',
      component: CustomerDevelopersRoute,
      subroutes: customerDeveloperRoutes,
    },
    {
      path: '/customer/activity',
      component: ActivityListing,
      rqps: ['size', 'orderBy', 'order', 'before', 'after'],
      allow: { audit_log: 'view' },
      exact: true,
    },
    {
      path: '/customer/manage/users/:userId',
      allow: { users: 'view' },
      rqps: ['size', 'orderBy', 'order'],
      component: Users,
    },
    {
      path: '/customer/manage',
      allowIfAll: [{ account_profile: 'view' }, { users: 'view' }],
      component: CustomerManageRoute,
      nav: {
        name: 'Manage',
        group: 'other',
        icon: <ManageIcon />,
      },
      subroutes: customerManageRoutes,
    },
    {
      path: '/customer/support',
      component: ContactSupport,
      allow: 'any',
      exact: true,
    },
    {
      path: '/customer/*',
      component: NotFound,
    },
  ];
};
